// ================= STYLE BRIEFS =================
//
// What each of the eight Styles is FOR, in two or three sentences, on the screens where a player
// picks one. deploy_handoff/after-the-bell-plan.md Phase H, from the 2026-09-05 persona review.
//
// WHY THIS FILE HAD TO BE WRITTEN RATHER THAN LOOKED UP. There was no per-Style description
// anywhere in the repo. js/cards.js's STYLE_LABELS is a display-name map and nothing more, the deck
// builder's Style picker said only "Your Style decides your colour and which cards you may use",
// and rules.html has no Styles section. So the martial-arts persona's first task -- choose a style
// from its description and predict how it will play -- failed at the first step, on a game whose
// whole pitch is that the styles fight differently. The identity was real in the cards and absent
// from every screen where it would have changed a decision.
//
// AUTHORED FROM THE CARD DATA, NOT FROM THE ART OR FROM OUTSIDE KNOWLEDGE. Every claim below is one
// the 14-card Style pool actually makes -- its range spread, its Attack categories, what its decks'
// own archetype lines say, and the Foundation passive it is paired with. The measured spreads at
// time of writing, which is what these sentences are describing:
//
//   Western Boxing   Punch 14/14        Pocket 14  Long  8  Clinch  1
//   Muay Thai        Kick 6 Elbow 3 Knee 2         Long  5  Pocket  9  Clinch 8
//   Shotokan Karate  Punch 11 Kick 3    Pocket 14  Long  5
//   Taekwondo        Kick 13/14         Long 13    Pocket 11  Clinch 0
//   Wing Chun        Pocket 12  Clinch 12  Long 1
//   Pencak Silat     Pocket 11  Clinch  9  Long 3
//   Wrestling        Clinch 12  Pocket  9  Grapple-led
//   Judo             Clinch 14  Pocket  6  Grapple-led
//
// A brief that stops being true when the pool moves is worse than none, because it is read at the
// moment of choosing and cannot be checked. tests/style-briefs-test.js holds the range claims
// against the live card data for exactly that reason -- if a rebalance moves a Style's centre of
// gravity, the sentence fails rather than quietly misleading somebody.
//
// THEY DESCRIBE THE DECKS, NOT THE MARTIAL ARTS. The evaluation playbook is explicit that the game
// must not imply every school or practitioner fights one way, and the honest framing is also the
// accurate one: these sentences are about a hand of cards in this engine. So they say what the
// cards do and where they want to be, and never what a person trained in the art "would" do.
//
// NO DEPENDENCIES BEYOND js/cards.js, deliberately and for the same reason js/style-passives.js has
// none: this is loaded by index.html, deck-builder.html, library.html and rules.html, and three of
// those four have no engine, no session and no bout.
(function () {
  'use strict';

  function escapeHtml(str) {
    return String(str == null ? '' : str)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // `gameplan` is the sentence that answers "what am I trying to do with this?"; `range` names where
  // the pool actually wants the fight, in the game's own three bands, and is the half a player can
  // check against the Range track two minutes later. Kept as separate fields rather than one
  // paragraph because the Setup browser has room for both and the builder's picker has room for one.
  var ENTRIES = {
    WesternBoxing: {
      style: 'WesternBoxing',
      range: 'Pocket, with the reach to work from Long.',
      gameplan: 'Hands only — every one of its fourteen cards is a Punch, and it is the one Style ' +
        'that gives up the Clinch entirely to be better in front of it. It trades reach and ' +
        'variety for volume: chains that convert a read into a fixed sequence, and enough ' +
        'Pocket coverage that it is rarely out of position.',
      feel: 'Rewards a player who wants to be right about what is coming next.'
    },
    MuayThai: {
      style: 'MuayThai',
      range: 'Long, Pocket and Clinch — the only Style at home in all three.',
      gameplan: 'Kicks at Long, knees and elbows once the fight closes, and grips to drag it there. ' +
        'Its pool is the widest in the game by Attack category, so it answers a range change ' +
        'instead of suffering one — and it is built to make the other fighter pay for every band ' +
        'they cross.',
      feel: 'Rewards a player happy to fight anywhere and impatient to close.'
    },
    ShotokanKarate: {
      style: 'ShotokanKarate',
      range: 'Pocket, decisively — every card in the pool wants it.',
      gameplan: 'Straight, committed attacks thrown from a settled base, mostly with the hands. It ' +
        'is the narrowest range profile of the eight and spends that narrowness on power: its ' +
        'Foundation pays +2 whenever an attack that wants one range only gets that range, and ' +
        'almost everything here wants one range only.',
      feel: 'Rewards a player who will hold a position and make the fight come to it.'
    },
    Taekwondo: {
      style: 'Taekwondo',
      range: 'Long first, Pocket second, and nothing at all in the Clinch.',
      gameplan: 'Thirteen of its fourteen cards are Kicks, and they are the fastest way into an ' +
        'exchange the game has. It scores from distance and steps off rather than standing in ' +
        'front of an answer, which also means being dragged into a Clinch is the losing shape of ' +
        'the bout rather than a rough patch.',
      feel: 'Rewards a player who would rather move than absorb.'
    },
    WingChun: {
      style: 'WingChun',
      range: 'Pocket and Clinch, held rather than crossed.',
      gameplan: 'Close-quarters occupation: take the space in front of the other fighter and keep ' +
        'answering from it, alternating high and low so the guard is always one level wrong. Its ' +
        'Foundation turns a clean catch into a lock on their next Intent, so defending well here ' +
        'is an attack on their plan rather than a pause in yours.',
      feel: 'Rewards a player who enjoys taking an option away.'
    },
    PencakSilat: {
      style: 'PencakSilat',
      range: 'Pocket and Clinch, entered on an angle.',
      gameplan: 'Evasion that ends somewhere useful. It dodges off the line rather than backing ' +
        'straight out, then answers with elbows, low limb attacks and redirects that turn the ' +
        'other fighter’s commitment into the opening. Fewer straight trades than its Foundation ' +
        'partner, and more exchanges won before contact.',
      feel: 'Rewards a player who wants to win the exchange by not being there.'
    },
    Wrestling: {
      style: 'Wrestling',
      range: 'Clinch, arrived at from the Pocket.',
      gameplan: 'Get hold of them and keep hold. Low Grapple entries, chained control, and a ' +
        'Foundation that makes every clean throw cost them their base — then punishes them again ' +
        'for still being Off-Balance. It is the most relentless pool in the game and the least ' +
        'interested in range at all.',
      feel: 'Rewards a player who wants to remove options rather than out-guess them.'
    },
    Judo: {
      style: 'Judo',
      range: 'Clinch, almost exclusively — all fourteen cards live there.',
      gameplan: 'Break their balance first, finish second. It is the most committed pool of the ' +
        'eight: it gives up the Pocket and the Long band almost entirely for grips and throws ' +
        'that end exchanges outright, including attacks that spend your own position to take ' +
        'theirs. Sharing Wrestling’s Foundation, it goes for the single decisive hold where ' +
        'Wrestling grinds.',
      feel: 'Rewards a player willing to risk a bad position for a finish.'
    }
  };

  function labelFor(style) {
    return (window.STYLE_LABELS && window.STYLE_LABELS[style]) || style;
  }

  function foundationOf(style) {
    var fam = (window.FAMILY_OF_STYLE || {})[style];
    return fam ? { id: fam, label: labelFor(fam) } : null;
  }

  function get(style) {
    var e = style && ENTRIES[style];
    if (!e) return null;
    // Composed on read rather than stored, so a Style renamed in js/cards.js cannot leave a stale
    // label frozen in this table -- the same rule js/style-passives.js follows for foundationLabel.
    var f = foundationOf(style);
    return {
      style: e.style, label: labelFor(style),
      range: e.range, gameplan: e.gameplan, feel: e.feel,
      foundation: f ? f.id : null, foundationLabel: f ? f.label : ''
    };
  }

  // In the order js/cards.js declares them, which is the order the Foundations pair up in -- so a
  // reader going down the Rules page meets Shotokan beside Taekwondo and Wrestling beside Judo,
  // and the shared passive is obvious without being pointed at.
  function all() {
    return Object.keys(window.STYLE_LABELS || {})
      .filter(function (k) { return !!ENTRIES[k]; })
      .map(get);
  }

  // The two-line form, for a picker or a deck browser. `opts.foundation` adds the Foundation name,
  // which the caller may already be printing beside it (the Setup browser does).
  function html(style, opts) {
    var b = get(style);
    if (!b) return '';
    var o = opts || {};
    var foundation = (o.foundation && b.foundationLabel)
      ? '<p class="sb-foundation"><span class="sb-key">Foundation</span> ' +
        escapeHtml(b.foundationLabel) + '</p>'
      : '';
    return '<div class="style-brief">' +
      '<p class="sb-range"><span class="sb-key">Range</span> ' + escapeHtml(b.range) + '</p>' +
      '<p class="sb-plan">' + escapeHtml(b.gameplan) + '</p>' +
      (o.feel === false ? '' : '<p class="sb-feel">' + escapeHtml(b.feel) + '</p>') +
      foundation +
      '</div>';
  }

  // One sentence, for somewhere with no room for three -- the deck-select blurb line, which is
  // clamped to two lines at 0.66rem.
  function oneLiner(style) {
    var b = get(style);
    return b ? b.feel : '';
  }

  window.StyleBriefs = {
    get: get, all: all, html: html, oneLiner: oneLiner,
    // Exported for the suite, which holds the range claims against the live card pool.
    ENTRIES: ENTRIES
  };
})();
